// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React from 'react';
import PropTypes from 'prop-types';
import { Typography, LinearProgress } from '@mui/material';
import sparkleIcon from 'Assets/sparkle.png';
import { formatValue } from 'Utils/deliverables';
import { Box, useTheme } from '@mui/system';
import { useAppSelector } from 'Hooks';
import stylesDef from './styles';

// The credit bar reads the account's one plan object, whatever the plan's origin (AUT-6571): a plan with a balance
// shows "{used} of {total} credits used", a pay-as-you-go plan its credits processed and, in the header, its rate.
// An Express AI subscription on an account with no plan keeps its own line.
const ProcessedAcreage = ({ headerComponent }) => {
  const theme = useTheme();
  const styles = stylesDef(theme);

  const plan = useAppSelector((state) => state.account.org?.plan);
  const expressAISubscription = useAppSelector((state) => state.subscription.expressAISubscription);

  const hasBalance = typeof plan?.credits?.total === 'number';
  const isPayg = !!plan && !hasBalance;
  const expressAIOnly = !plan && !!expressAISubscription?.name;

  let usedCredits = 0;
  let totalCredits = 0;
  if (hasBalance) {
    usedCredits = plan?.credits?.used || 0;
    totalCredits = plan?.credits?.total || 0;
  } else if (expressAIOnly) {
    usedCredits = expressAISubscription?.expressAIUsedCredits || 0;
    totalCredits = expressAISubscription?.expressAIStartingCredits || 0;
  }
  const rawPercent = totalCredits > 0 ? (usedCredits / totalCredits) * 100 : 0;
  const creditOverage = rawPercent > 100;
  const creditsPercent = Math.min(rawPercent, 100);
  const paygRate = plan?.pricePerCredit;

  return (
    <>
      {isPayg && (
        <>
          <Box sx={{ ...styles.noCommitText, ...(headerComponent && styles.noCommitHeader) }}>
            <img src={sparkleIcon} style={styles.imgIcon} alt="sparkle" />
            <Typography variant="subtitle1" component="span" sx={styles.boldtext}>
              {formatValue(plan?.credits?.used || 0)}
              {' '}
              <Typography component="span" variant="subtitle1">credits processed this year</Typography>
            </Typography>
          </Box>
          {headerComponent && typeof paygRate === 'number' && (
            <Typography variant="body2" sx={styles.rateText}>
              Your current rate is
              <Typography component="span" variant="body2" sx={styles.boldtext}>
                {` $${formatValue(paygRate)}`}
              </Typography>
              {' per credit'}
            </Typography>
          )}
        </>
      )}
      {(hasBalance || expressAIOnly) && (
        <>
          <Typography variant="subtitle1" color="textSecondary" component="span" sx={styles.boldtext}>
            <Typography component="span" variant="subtitle1" color="primary">
              {formatValue(usedCredits)}
            </Typography>
            {` of ${formatValue(totalCredits)} credits `}
          </Typography>
          <Typography variant="body1" component="span">
            {hasBalance ? 'used' : 'processed'}
          </Typography>
          <LinearProgress
            variant="buffer"
            value={creditsPercent}
            valueBuffer={creditsPercent}
            sx={{ ...styles.dashedBar, ...(creditOverage ? styles.noAcreage : styles.hasAcreage) }}
          />
        </>
      )}
    </>
  );
};

ProcessedAcreage.propTypes = {
  headerComponent: PropTypes.bool,
};

export default ProcessedAcreage;
